import type {
  CommercialOfferDraftWrite,
  CommercialOfferPublicationReview,
  CommercialPublicationRequest,
  CommercialOfferOwnerView,
} from "@bw/shared/types/generated";
import type { ModuleOfferV2EditorProps } from "./moduleOfferAuthoringTypes";
import { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ModuleOfferEconomics,
  ModuleOfferExactDocument,
  moduleOfferGateLabels,
} from "@bw/shared/components";

export type ModuleOfferEditorProps = ModuleOfferV2EditorProps;
export function ModuleOfferEditor({
  value,
  onChange,
  onSave,
  isPending,
  error,
}: ModuleOfferEditorProps) {
  type Allowance = {
    unit: string;
    included: string;
    maximum: string;
    period: string;
    over: string;
  };
  // The controller's keyed explicit hydration owns this pin. A temporarily
  // incomplete parent value must not erase an existing private reference.
  const [annualApproval] = useState(() => value?.economics.annual_discount_approval_reference ?? null);
  const [fields, setFields] = useState(() => ({
    title: value?.title ?? "",
    module: value?.module_key ?? "",
    capabilityVersion: value?.capability_version ?? "",
    capabilityKeys: value?.capability_keys.join("\n") ?? "",
    audience: value ? (value.audience_org_id === null ? "all" : "org") : "",
    org: value?.audience_org_id ?? "",
    cadence: value?.economics.cadence ?? "",
    price: value?.economics.billing_period_total
      ? String(value.economics.billing_period_total.amount_minor)
      : "",
    currency: value?.economics.billing_period_total.currency ?? "",
    renewalMode: value?.economics.renewal_mode ?? "",
    unit: value?.economics.billable_unit ?? "",
    quantity:
      value?.economics.unit_quantity == null
        ? ""
        : String(value.economics.unit_quantity),
    periods:
      value?.economics.contract_periods == null
        ? ""
        : String(value.economics.contract_periods),
    trial:
      value?.economics.trial_days == null
        ? ""
        : String(value.economics.trial_days),
    grace:
      value?.economics.grace_days == null
        ? ""
        : String(value.economics.grace_days),
    renewal: value?.economics.renewal_policy_reference ?? "",
    refund: value?.economics.refund_policy_reference ?? "",
    termination: value?.economics.termination_policy_reference ?? "",
    retention: value?.economics.retention_policy_reference ?? "",
    content: value?.document.content ?? "",
    media: value?.document.media_type ?? "",
    template: value?.document.template_key ?? "",
    templateVersion: value?.document.template_version ?? "",
    renderingVersion: value?.document.rendering_version ?? "",
    legalParty: value?.document.legal_party_reference ?? "",
    jurisdictions: value?.document.jurisdiction_references.join("\n") ?? "",
    language: value?.document.language ?? "",
    fees: value?.fee_policy_version_ids.join("\n") ?? "",
    from: value?.effective_from ?? "",
    until: value?.effective_until ?? "",
  }));
  const [allowances, setAllowances] = useState<Allowance[]>(
    () =>
      value?.economics.allowances.map((item) => ({
        unit: item.unit_key,
        included:
          item.included_quantity == null ? "" : String(item.included_quantity),
        maximum:
          item.maximum_quantity == null ? "" : String(item.maximum_quantity),
        period: item.period,
        over: item.over_limit_behavior,
      })) ?? [],
  );
  const [noAllowances, setNoAllowances] = useState(
    Boolean(value && !value.economics.allowances.length),
  );
  const [noFees, setNoFees] = useState(
    Boolean(value && !value.fee_policy_version_ids.length),
  );
  const lines = (text: string) =>
    text
      .split(/[,\n]/)
      .map((line) => line.trim())
      .filter(Boolean);
  const bounded = (text: string) =>
    text.trim().length > 0 && text.trim().length <= 128;
  const integer = (text: string, minimum = 0): number | null | undefined =>
    text === ""
      ? null
      : /^\d+$/.test(text) &&
          Number.isSafeInteger(Number(text)) &&
          Number(text) >= minimum
        ? Number(text)
        : undefined;
  const timestamp = (text: string) =>
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/i.test(
      text,
    ) && Number.isFinite(Date.parse(text));
  const uuid = (text: string) =>
    /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(text);
  const oneOf = <T extends string,>(text: string, choices: readonly T[]): text is T =>
    choices.some(choice => choice === text);
  function build(
    next = fields,
    limits = allowances,
    emptyLimits = noAllowances,
    emptyFees = noFees,
  ): CommercialOfferDraftWrite | null {
    const keys = lines(next.capabilityKeys),
      jurisdictions = lines(next.jurisdictions),
      fees = lines(next.fees);
    if (
      !next.title.trim() ||
      next.title.length > 255 ||
      !oneOf(next.module, ["academy", "insights", "competitions"] as const) ||
      !bounded(next.capabilityVersion) ||
      !keys.length ||
      !keys.every(bounded) ||
      !oneOf(next.audience, ["all", "org"] as const) ||
      (next.audience === "org" && !uuid(next.org)) ||
      !oneOf(next.cadence, ["monthly", "annual"] as const) ||
      !oneOf(next.renewalMode, ["fixed_term", "auto_renew"] as const) ||
      !oneOf(next.media, ["text/plain", "text/html"] as const) ||
      !next.content ||
      ![
        next.template,
        next.templateVersion,
        next.renderingVersion,
        next.legalParty,
        next.language,
      ].every(bounded) ||
      !jurisdictions.length ||
      !jurisdictions.every(bounded) ||
      !timestamp(next.from) ||
      (next.until &&
        (!timestamp(next.until) ||
          Date.parse(next.until) <= Date.parse(next.from))) ||
      (!fees.length && !emptyFees) ||
      !fees.every(uuid) ||
      (!limits.length && !emptyLimits)
    )
      return null;
    const price = integer(next.price),
      quantity = integer(next.quantity, 1),
      periods = integer(next.periods, 1),
      trial = integer(next.trial),
      grace = integer(next.grace);
    if (
      price == null || quantity == null || trial == null || grace == null || periods === undefined ||
      (next.renewalMode === "fixed_term" ? periods === null : periods !== null) ||
      !/^[a-z]{3}$/.test(next.currency) ||
      ![next.unit, next.renewal, next.refund, next.termination, next.retention].every(bounded)
    )
      return null;
    const parsedLimits: CommercialOfferDraftWrite["economics"]["allowances"] =
      [];
    for (const item of limits) {
      const included = integer(item.included),
        maximum = integer(item.maximum);
      if (
        !bounded(item.unit) ||
        !oneOf(item.period, ["billing_period", "contract", "not_applicable"] as const) ||
        !oneOf(item.over, ["block_new_operations", "not_applicable"] as const) ||
        included === undefined ||
        maximum === undefined ||
        (included !== null && maximum !== null && included > maximum)
      )
        return null;
      parsedLimits.push({
        unit_key: item.unit.trim(),
        included_quantity: included,
        maximum_quantity: maximum,
        period: item.period,
        over_limit_behavior: item.over,
      });
    }
    return {
      terms_format: "software_terms_v2",
      title: next.title.trim(),
      module_key: next.module,
      capability_version: next.capabilityVersion.trim(),
      capability_keys: keys,
      audience_org_id: next.audience === "all" ? null : next.org,
      economics: {
        cadence: next.cadence,
        price_definition: "billing_period_total",
        billing_period_total: { amount_minor: price, currency: next.currency },
        billable_unit: next.unit.trim(),
        unit_quantity: quantity,
        renewal_mode: next.renewalMode,
        contract_periods: periods,
        allowances: parsedLimits,
        trial_days: trial,
        grace_days: grace,
        renewal_policy_reference: next.renewal.trim(),
        refund_policy_reference: next.refund.trim(),
        termination_policy_reference: next.termination.trim(),
        retention_policy_reference: next.retention.trim(),
        annual_discount_approval_reference: annualApproval,
      },
      document: {
        content: next.content,
        media_type: next.media,
        template_key: next.template.trim(),
        template_version: next.templateVersion.trim(),
        rendering_version: next.renderingVersion.trim(),
        legal_party_reference: next.legalParty.trim(),
        jurisdiction_references: jurisdictions,
        language: next.language.trim(),
      },
      fee_policy_version_ids: fees,
      effective_from: next.from,
      effective_until: next.until || null,
    };
  }
  function change(field: keyof typeof fields, text: string) {
    const next = { ...fields, [field]: text,
      ...(field === "renewalMode" && text === "auto_renew" ? { periods: "" } : {}) };
    setFields(next);
    const draft = build(next);
    onChange(draft);
  }
  const required = new Set([
    "title",
    "module",
    "capabilityVersion",
    "capabilityKeys",
    "audience",
    "org",
    "cadence",
    "renewalMode",
    "price", "currency", "unit", "quantity", "trial", "grace",
    "renewal", "refund", "termination", "retention",
    "content",
    "media",
    "template",
    "templateVersion",
    "renderingVersion",
    "legalParty",
    "jurisdictions",
    "language",
    "from",
  ]);
  const text = (
    field: keyof typeof fields,
    label: string,
    multiline = false,
    helperText?: string,
  ) => (
    <TextField
      key={field}
      required={required.has(field)}
      size="small"
      label={label}
      value={fields[field]}
      disabled={isPending}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      helperText={helperText}
      onChange={(e) => change(field, e.target.value)}
    />
  );
  const select = (
    field: keyof typeof fields,
    label: string,
    options: readonly string[],
  ) => (
    <TextField
      key={field}
      required={required.has(field)}
      select
      size="small"
      label={label}
      value={fields[field]}
      disabled={isPending}
      onChange={(e) => change(field, e.target.value)}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option === "all"
            ? "All organizations"
            : option === "org"
              ? "One organization"
              : option.replaceAll("_", " ")}
        </MenuItem>
      ))}
    </TextField>
  );
  const valid = build();
  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {text("title", "Offer title")}
      {select("module", "Module", ["academy", "insights", "competitions"])}
      {text("capabilityVersion", "Capability version")}
      {text(
        "capabilityKeys",
        "Capability keys",
        true,
        "One source-defined capability per line; naming it grants no access.",
      )}
      {select("audience", "Audience", ["all", "org"])}
      {fields.audience === "org" ? text("org", "Organization reference") : null}
      <Typography variant="subtitle2">Proposed economics</Typography>
      <Typography variant="body2" color="text.secondary">
        Required economics must be explicit. Draft inputs do not establish approval.
      </Typography>
      {select("cadence", "Billing cadence", ["monthly", "annual"])}
      {text("price", "Billing period total (minor units)", false, "Total for the billing period, not multiplied by unit quantity.")}
      {select("currency", "Billing currency", [...new Set(["aud", "cad", "eur", "usd", ...(fields.currency ? [fields.currency] : [])])])}
      {text("unit", "Billable unit")}
      {text("quantity", "Unit quantity")}
      {select("renewalMode", "Renewal mode", ["fixed_term", "auto_renew"])}
      <TextField size="small" label="Contract periods" value={fields.periods}
        required={fields.renewalMode === "fixed_term"} disabled={isPending || fields.renewalMode === "auto_renew"}
        helperText={fields.renewalMode === "auto_renew" ? "No fixed-term cap for explicit auto-renew." : "Explicit positive billing-period count for a fixed term."}
        onChange={event => change("periods", event.target.value)} />
      {text("trial", "Trial days")}
      {text("grace", "Grace days")}
      {text("renewal", "Renewal policy reference")}
      {text("refund", "Refund policy reference")}
      {text("termination", "Termination policy reference")}
      {text("retention", "Retention policy reference")}
      <Typography variant="subtitle2">Allowances and limits</Typography>
      {allowances.map((item, index) => (
        <Stack key={index} spacing={1}>
          {(["unit", "included", "maximum"] as const).map((field) => (
            <TextField
              key={field}
              size="small"
              label={
                {
                  unit: "Allowance unit",
                  included: "Included quantity",
                  maximum: "Maximum quantity",
                }[field]
              }
              value={item[field]}
              disabled={isPending}
              onChange={(e) => {
                const next = allowances.map((row, i) =>
                  i === index ? { ...row, [field]: e.target.value } : row,
                );
                setAllowances(next);
                const draft = build(fields, next);
                onChange(draft);
              }}
            />
          ))}
          {(["period", "over"] as const).map((field) => (
            <TextField
              key={field}
              select
              size="small"
              label={
                field === "period" ? "Allowance period" : "Over-limit behavior"
              }
              value={item[field]}
              disabled={isPending}
              onChange={(e) => {
                const next = allowances.map((row, i) =>
                  i === index ? { ...row, [field]: e.target.value } : row,
                );
                setAllowances(next);
                const draft = build(fields, next);
                onChange(draft);
              }}
            >
              {(field === "period"
                ? ["billing_period", "contract", "not_applicable"]
                : ["block_new_operations", "not_applicable"]
              ).map((option) => (
                <MenuItem key={option} value={option}>
                  {option.replaceAll("_", " ")}
                </MenuItem>
              ))}
            </TextField>
          ))}
          <Button
            size="small"
            disabled={isPending}
            onClick={() => {
              const next = allowances.filter((_, i) => i !== index);
              setAllowances(next);
              const draft = build(fields, next);
              onChange(draft);
            }}
          >
            Remove allowance
          </Button>
        </Stack>
      ))}
      {!allowances.length ? (
        <FormControlLabel
          label="No allowance entries proposed (not unlimited use)"
          control={
            <Checkbox
              checked={noAllowances}
              disabled={isPending}
              onChange={(e) => {
                setNoAllowances(e.target.checked);
                const draft = build(fields, allowances, e.target.checked);
                onChange(draft);
              }}
            />
          }
        />
      ) : null}
      <Button
        size="small"
        variant="outlined"
        disabled={isPending}
        onClick={() => {
          const next = [
            ...allowances,
            { unit: "", included: "", maximum: "", period: "", over: "" },
          ];
          setAllowances(next);
          onChange(build(fields, next));
        }}
      >
        Add allowance
      </Button>
      <Typography variant="subtitle2">Exact document source</Typography>
      {text("content", "Document content", true)}
      {select("media", "Document media type", ["text/plain", "text/html"])}
      {text("template", "Template reference")}
      {text("templateVersion", "Template version")}
      {text("renderingVersion", "Rendering version")}
      {text("legalParty", "Legal party reference")}
      {text("jurisdictions", "Jurisdiction references", true)}
      {text("language", "Document language")}
      {text("fees", "Published fee edition references", true)}
      {!fields.fees.trim() ? (
        <FormControlLabel
          label="No fee editions referenced"
          control={
            <Checkbox
              checked={noFees}
              disabled={isPending}
              onChange={(e) => {
                setNoFees(e.target.checked);
                const draft = build(
                  fields,
                  allowances,
                  noAllowances,
                  e.target.checked,
                );
                onChange(draft);
              }}
            />
          }
        />
      ) : null}
      {text(
        "from",
        "Effective from (UTC timestamp)",
        false,
        "Include Z or an explicit UTC offset.",
      )}
      {text(
        "until",
        "Effective until (optional UTC timestamp)",
        false,
        "Include Z or an explicit UTC offset.",
      )}
      <Typography variant="body2" color="text.secondary">
        Draft content and references are proposals, not approval evidence.
      </Typography>
      <Button
        size="small"
        variant="contained"
        disabled={isPending || !valid || value === null}
        onClick={() => {
          if (!isPending && valid && value) onSave();
        }}
      >
        Save draft
      </Button>
    </Stack>
  );
}

export interface ModuleOfferPublicationReviewProps {
  review: CommercialOfferPublicationReview;
  attempt: CommercialPublicationRequest | null;
  isPending: boolean;
  error: string | null;
  onPublish: (request: CommercialPublicationRequest) => void;
}
export function ModuleOfferPublicationFacts({
  review,
  attempt,
  isPending,
  error,
  onPublish,
}: ModuleOfferPublicationReviewProps) {
  const ready =
    review.source_available &&
    review.environment_allowed === true &&
    !review.unresolved_gates.length &&
    !review.blockers.length;
  const exact =
    attempt &&
    attempt.expected_revision === review.candidate.draft_revision &&
    attempt.environment === review.candidate.environment;
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        Draft revision {review.candidate.draft_revision} ·{" "}
        {review.candidate.environment}
      </Typography>
      {review.candidate.document_sha256 ? (
        <>
          <Typography variant="caption">
            Exact candidate document SHA-256
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {review.candidate.document_sha256}
          </Typography>
        </>
      ) : null}
      {!review.source_available ? (
        <Alert severity="warning">
          Publication approval source unavailable. Actual reviewed product,
          legal and release artifacts are required.
        </Alert>
      ) : null}
      {review.environment_allowed === false ? (
        <Alert severity="warning">
          This publication environment is not allowed by the current source.
        </Alert>
      ) : null}
      {review.unresolved_gates.map((gate) => (
        <Typography key={gate} variant="body2">
          Unresolved: {moduleOfferGateLabels[gate] ?? "Source decision"}
        </Typography>
      ))}
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {ready && exact ? (
        <Button
          size="small"
          variant="contained"
          disabled={isPending}
          onClick={() => {
            if (!isPending && attempt && exact) onPublish(attempt);
          }}
        >
          {error
            ? "Retry original publication"
            : "Publish exact reviewed revision"}
        </Button>
      ) : null}
      <Typography variant="body2" color="text.secondary">
        A candidate or draft is not approval. Publication rechecks the current
        source and does not activate a licence.
      </Typography>
    </Stack>
  );
}

export interface ModuleOfferVersionFactsProps {
  version: CommercialOfferOwnerView;
}
export function ModuleOfferVersionFacts({
  version,
}: ModuleOfferVersionFactsProps) {
  if (version.terms_format !== "legacy_fixed_v1" && version.terms_format !== "software_terms_v2")
    return <Alert severity="warning">Stored module offer format unavailable.</Alert>;
  return (
    <Stack spacing={2.5}>
      <Typography component="h2" variant="h6">
        {version.title}
      </Typography>
      {version.terms_format === "legacy_fixed_v1"
        ? <ModuleOfferEconomics termsFormat="legacy_fixed_v1" terms={version.economics} />
        : <ModuleOfferEconomics termsFormat="software_terms_v2" terms={version.economics} />}
      <Typography variant="body2">
        Published {new Date(version.published_at).toLocaleString()} ·{" "}
        {version.environment}
      </Typography>
      <ModuleOfferExactDocument document={version.document} />
      <Typography variant="body2" color="text.secondary">
        Immutable published terms; software purchase and activation are not
        enabled here.
      </Typography>
    </Stack>
  );
}
